<template>
  <Layout>
    <PageHeader :title="'My Profile'" :items="breadcrumbs" />
    <div class="member-profile" :style="{ paddingBottom: '64px' }">

      <!-- Loading state -->
      <div v-if="profileLoading" class="text-center py-5">
        <b-spinner variant="primary" />
      </div>

      <!-- Profile content -->
      <div v-else-if="memberProfile" class="text-center py-4">
        <b-avatar
          :text="initials"
          variant="primary"
          size="72px"
          class="mb-3"
        />

        <h1 class="profile-name">{{ memberProfile.member_name }}</h1>
        <p class="profile-member-number font-monospace text-muted">
          {{ memberProfile.member_number }}
        </p>

        <div class="profile-badges mb-4">
          <b-badge variant="primary" class="mr-2 text-capitalize">
            {{ memberProfile.membership_tier }}
          </b-badge>
          <b-badge :variant="memberProfile.membership_active ? 'success' : 'danger'">
            {{ memberProfile.membership_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </div>

        <b-card class="profile-details mx-auto text-left" style="max-width: 400px;">
          <div class="detail-row d-flex justify-content-between mb-3">
            <span class="text-muted">Club</span>
            <span>{{ memberProfile.branch_name || 'N/A' }}</span>
          </div>
          <div class="detail-row d-flex justify-content-between mb-3">
            <span class="text-muted">Contact</span>
            <span>{{ memberProfile.contact_no || 'N/A' }}</span>
          </div>
          <div class="detail-row d-flex justify-content-between mb-3">
            <span class="text-muted">Email</span>
            <span>{{ memberProfile.email || 'N/A' }}</span>
          </div>
          <div v-if="memberProfile.accepted_membership_date" class="detail-row d-flex justify-content-between">
            <span class="text-muted">Member since</span>
            <span>{{ formatDate(memberProfile.accepted_membership_date) }}</span>
          </div>
        </b-card>
      </div>

      <!-- Error state -->
      <div v-else class="text-center py-5">
        <p class="text-muted">Unable to load profile.</p>
        <b-button variant="primary" @click="fetchMemberProfile">Retry</b-button>
      </div>
    </div>

    <cart-bottom-nav
      active-tab="profile"
      :cart-count="cartItemCount"
      @navigate="handleNavigate"
    />
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { memberOrderingComputed, memberOrderingMethods } from "@/state/helpers";
import CartBottomNav from "@/components/member/cartBottomNav.vue";

export default {
  components: { Layout, PageHeader, CartBottomNav },
  page: { title: "My Profile" },
  computed: {
    ...memberOrderingComputed,
    breadcrumbs() {
      return [{ text: "My Profile", active: true }];
    },
    initials() {
      if (!this.memberProfile || !this.memberProfile.member_name) return "?";
      return this.memberProfile.member_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
  },
  methods: {
    ...memberOrderingMethods,
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
    },
    handleNavigate(tab) {
      const routes = {
        shop: "member-shop",
        cart: "member-cart",
        orders: "member-orders",
        profile: "member-profile",
      };
      if (routes[tab] && this.$route.name !== routes[tab]) {
        this.$router.push({ name: routes[tab] });
      }
    },
  },
  created() {
    this.fetchMemberProfile();
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/brand";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/surfaces";

.profile-name {
  font-size: $type-h1;
  font-weight: 600;
  margin-bottom: $space-1;
}

.profile-member-number {
  font-size: $type-body-lg;
}

.profile-details {
  background: $surface-card;
}

.detail-row {
  font-size: $type-body;
}
</style>
